import { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./AuthPopup.css";
import { API_URL } from "../services/api";

const PASOS = [
  { titulo: "Crea tu cuenta", sub: "Empezamos por lo básico." },
  { titulo: "Cómo localizarte", sub: "Tu entrenador lo necesita para organizar las sesiones." },
  { titulo: "Tu punto de partida", sub: "Con esto se monta tu primera rutina." }
];

const CAMPOS_POR_PASO = [
  ["firstName", "lastName", "email", "password"],
  ["phone", "location"],
  ["weight", "height"]
];

const VACIO = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  phone: "",
  location: "",
  weight: "",
  height: "",
  goal: "bajar"
};

function validarCampo(name, value) {
  const v = String(value ?? "").trim();

  switch (name) {
    case "firstName":
      return v ? "" : "Escribe tu nombre.";
    case "lastName":
      return v ? "" : "Escribe tu apellido.";
    case "email":
      if (!v) return "Escribe tu correo.";
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "" : "Ese correo no parece válido.";
    case "password":
      if (!v) return "Elige una contraseña.";
      return v.length >= 8 ? "" : "Usa al menos 8 caracteres.";
    case "phone":
      if (!v) return "Escribe tu teléfono.";
      return /^[+\d][\d\s.-]{6,}$/.test(v) ? "" : "Ese teléfono no parece válido.";
    case "location":
      return v ? "" : "Escribe tu ciudad.";
    case "weight": {
      if (!v) return "Escribe tu peso.";
      const n = Number(v);
      return n > 20 && n < 400 ? "" : "El peso debe estar entre 20 y 400 kg.";
    }
    case "height": {
      if (!v) return "Escribe tu altura.";
      const n = Number(v);
      return n > 90 && n < 260 ? "" : "La altura debe estar entre 90 y 260 cm.";
    }
    default:
      return "";
  }
}

function fuerzaContrasena(pwd) {
  if (!pwd) return { nivel: 0, texto: "", color: "transparent" };
  let puntos = 0;
  if (pwd.length >= 8) puntos++;
  if (pwd.length >= 12) puntos++;
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) puntos++;
  if (/\d/.test(pwd)) puntos++;
  if (/[^\w\s]/.test(pwd)) puntos++;

  if (puntos <= 2) return { nivel: 33, texto: "Débil", color: "var(--t-danger)" };
  if (puntos === 3) return { nivel: 66, texto: "Aceptable", color: "var(--t-amber)" };
  return { nivel: 100, texto: "Fuerte", color: "var(--t-green-ink)" };
}

function RegisterPopup({ onClose, onGoToLogin }) {
  const [paso, setPaso] = useState(0);
  const [form, setForm] = useState(VACIO);
  const [errores, setErrores] = useState({});
  const [errorServidor, setErrorServidor] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);
  const [verPwd, setVerPwd] = useState(false);
  const primerCampoRef = useRef(null);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // El foco acompaña al paso
  useEffect(() => {
    primerCampoRef.current?.focus();
  }, [paso]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // El error se retira en cuanto el campo pasa a ser válido
    if (errores[name]) {
      const msg = validarCampo(name, value);
      if (!msg) setErrores((prev) => ({ ...prev, [name]: "" }));
    }
    setErrorServidor("");
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const msg = validarCampo(name, value);
    setErrores((prev) => ({ ...prev, [name]: msg }));
  };

  const validarPaso = (indice) => {
    const fallos = {};
    CAMPOS_POR_PASO[indice].forEach((campo) => {
      const msg = validarCampo(campo, form[campo]);
      if (msg) fallos[campo] = msg;
    });
    setErrores((prev) => ({ ...prev, ...fallos }));
    return Object.keys(fallos).length === 0;
  };

  const siguiente = () => {
    if (!validarPaso(paso)) return;
    setPaso((p) => p + 1);
  };

  const atras = () => {
    setErrorServidor("");
    setPaso((p) => Math.max(0, p - 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (paso < PASOS.length - 1) {
      siguiente();
      return;
    }
    if (!validarPaso(paso)) return;

    setEnviando(true);
    setErrorServidor("");

    try {
      await axios.post(`${API_URL}/users/register`, {
        ...form,
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        location: form.location.trim(),
        weight: Number(form.weight),
        height: Number(form.height)
      });
      setListo(true);
    } catch (err) {
      // El backend responde { error } en 400 y { message } en otros casos
      const data = err.response?.data || {};
      const msg = data.error || data.message || "";

      if (/ya está registrado|ya registrado/i.test(msg)) {
        setPaso(0);
        setErrores((prev) => ({
          ...prev,
          email: "Ese correo ya tiene cuenta. Inicia sesión."
        }));
      } else if (msg) {
        setErrorServidor(msg);
      } else if (err.response) {
        setErrorServidor("No se pudo crear la cuenta. Revisa los datos e inténtalo de nuevo.");
      } else {
        setErrorServidor("No hay conexión con el servidor. Inténtalo en un momento.");
      }
    } finally {
      setEnviando(false);
    }
  };

  const fuerza = fuerzaContrasena(form.password);

  if (listo) {
    return (
      <div className="auth-overlay" onClick={onClose}>
        <div
          className="auth-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="auth-done-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="auth-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
          <div className="auth-done">
            <div className="auth-done-mark" aria-hidden="true">✓</div>
            <h2 id="auth-done-title">Cuenta creada</h2>
            <p>
              Tu entrenador revisa tu ficha antes de darte acceso. Te avisamos en cuanto
              esté aprobada.
            </p>
            <button type="button" className="auth-btn auth-btn-primary" onClick={onClose}>
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  }

  const esUltimo = paso === PASOS.length - 1;

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div
        className="auth-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-register-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="auth-close" onClick={onClose} aria-label="Cerrar">
          ×
        </button>

        <h2 className="auth-title" id="auth-register-title">
          {PASOS[paso].titulo}
        </h2>
        <p className="auth-sub">{PASOS[paso].sub}</p>

        <div className="auth-progress" role="presentation">
          {PASOS.map((p, i) => (
            <span
              key={p.titulo}
              className={
                "auth-progress-step" +
                (i < paso ? " is-done" : "") +
                (i === paso ? " is-current" : "")
              }
            />
          ))}
        </div>
        <p className="auth-step-count">
          Paso {paso + 1} de {PASOS.length}
        </p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {paso === 0 && (
            <>
              <div className="auth-row">
                <div className={"auth-field" + (errores.firstName ? " has-error" : "")}>
                  <label htmlFor="reg-firstName">Nombre</label>
                  <input
                    id="reg-firstName"
                    ref={primerCampoRef}
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={form.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errores.firstName)}
                  />
                  {errores.firstName && (
                    <span className="auth-field-error">{errores.firstName}</span>
                  )}
                </div>

                <div className={"auth-field" + (errores.lastName ? " has-error" : "")}>
                  <label htmlFor="reg-lastName">Apellido</label>
                  <input
                    id="reg-lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errores.lastName)}
                  />
                  {errores.lastName && (
                    <span className="auth-field-error">{errores.lastName}</span>
                  )}
                </div>
              </div>

              <div className={"auth-field" + (errores.email ? " has-error" : "")}>
                <label htmlFor="reg-email">Correo electrónico</label>
                <input
                  id="reg-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={Boolean(errores.email)}
                />
                {errores.email && <span className="auth-field-error">{errores.email}</span>}
              </div>

              <div className={"auth-field" + (errores.password ? " has-error" : "")}>
                <label htmlFor="reg-password">Contraseña</label>
                <div className="auth-password">
                  <input
                    id="reg-password"
                    name="password"
                    type={verPwd ? "text" : "password"}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errores.password)}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setVerPwd((v) => !v)}
                  >
                    {verPwd ? "Ocultar" : "Ver"}
                  </button>
                </div>
                {form.password ? (
                  <div className="auth-strength">
                    <span className="auth-strength-track">
                      <span
                        className="auth-strength-fill"
                        style={{ width: fuerza.nivel + "%", background: fuerza.color }}
                      />
                    </span>
                    <span className="auth-strength-label">{fuerza.texto}</span>
                  </div>
                ) : (
                  <span className="auth-field-hint">Mínimo 8 caracteres.</span>
                )}
                {errores.password && (
                  <span className="auth-field-error">{errores.password}</span>
                )}
              </div>
            </>
          )}

          {paso === 1 && (
            <>
              <div className={"auth-field" + (errores.phone ? " has-error" : "")}>
                <label htmlFor="reg-phone">Teléfono</label>
                <input
                  id="reg-phone"
                  ref={primerCampoRef}
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={Boolean(errores.phone)}
                />
                {errores.phone && <span className="auth-field-error">{errores.phone}</span>}
              </div>

              <div className={"auth-field" + (errores.location ? " has-error" : "")}>
                <label htmlFor="reg-location">Ciudad</label>
                <input
                  id="reg-location"
                  name="location"
                  type="text"
                  autoComplete="address-level2"
                  value={form.location}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  aria-invalid={Boolean(errores.location)}
                />
                {errores.location && (
                  <span className="auth-field-error">{errores.location}</span>
                )}
              </div>
            </>
          )}

          {paso === 2 && (
            <>
              <div className="auth-row">
                <div className={"auth-field" + (errores.weight ? " has-error" : "")}>
                  <label htmlFor="reg-weight">Peso (kg)</label>
                  <input
                    id="reg-weight"
                    ref={primerCampoRef}
                    name="weight"
                    type="number"
                    inputMode="decimal"
                    min="20"
                    max="400"
                    step="0.1"
                    value={form.weight}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errores.weight)}
                  />
                  {errores.weight && (
                    <span className="auth-field-error">{errores.weight}</span>
                  )}
                </div>

                <div className={"auth-field" + (errores.height ? " has-error" : "")}>
                  <label htmlFor="reg-height">Altura (cm)</label>
                  <input
                    id="reg-height"
                    name="height"
                    type="number"
                    inputMode="numeric"
                    min="90"
                    max="260"
                    value={form.height}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errores.height)}
                  />
                  {errores.height && (
                    <span className="auth-field-error">{errores.height}</span>
                  )}
                </div>
              </div>

              <fieldset className="auth-fieldset">
                <legend>¿Qué quieres conseguir?</legend>
                <div className="auth-choices">
                  <label
                    className={
                      "auth-choice" + (form.goal === "bajar" ? " is-selected" : "")
                    }
                  >
                    <input
                      type="radio"
                      name="goal"
                      value="bajar"
                      checked={form.goal === "bajar"}
                      onChange={handleChange}
                    />
                    <span className="auth-choice-title">Bajar de peso</span>
                    <span className="auth-choice-note">Déficit y más trabajo aeróbico</span>
                  </label>

                  <label
                    className={
                      "auth-choice" + (form.goal === "subir" ? " is-selected" : "")
                    }
                  >
                    <input
                      type="radio"
                      name="goal"
                      value="subir"
                      checked={form.goal === "subir"}
                      onChange={handleChange}
                    />
                    <span className="auth-choice-title">Ganar masa</span>
                    <span className="auth-choice-note">Superávit y más carga</span>
                  </label>
                </div>
              </fieldset>
            </>
          )}

          {errorServidor && (
            <p className="auth-alert auth-alert-error" role="alert">
              {errorServidor}
            </p>
          )}

          <div className="auth-actions">
            {paso > 0 && (
              <button type="button" className="auth-btn auth-btn-back" onClick={atras}>
                Atrás
              </button>
            )}
            <button type="submit" className="auth-btn auth-btn-primary" disabled={enviando}>
              {enviando && <span className="auth-spinner" aria-hidden="true" />}
              {enviando ? "Creando cuenta" : esUltimo ? "Crear cuenta" : "Continuar"}
            </button>
          </div>
        </form>

        {onGoToLogin && (
          <p className="auth-switch">
            ¿Ya tienes cuenta? <button type="button" onClick={onGoToLogin}>Inicia sesión</button>
          </p>
        )}
      </div>
    </div>
  );
}

export default RegisterPopup;
